import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import ConsentModal from './components/ConsentModal';
import IntakeForm from './components/IntakeForm';

const CALENDLY_URL = 'https://calendly.com/jay-vazcular/new-meeting';

interface Package {
  id: string;
  name: string;
  icon: string;
  description: string;
  features: string[];
  popular?: boolean;
  color: string;
}

const packages: Package[] = [
  {
    id: 'package1',
    name: 'Heart Disease Foundations & Risk Assessment',
    icon: 'ri-heart-pulse-line',
    description: 'Comprehensive understanding of heart disease fundamentals and personalized risk evaluation.',
    features: [
      'Understanding Heart Disease',
      'Risk Factors & Disease Drivers',
      'Who Is at Risk?',
      'Age, Ethnicity & Demographic Impact',
      'Misconceptions About Heart Disease'
    ],
    color: '#f04e58'
  },
  {
    id: 'package2',
    name: 'Surgical Decision & Hospital Selection Advisory',
    icon: 'ri-hospital-line',
    description: 'Expert guidance on surgical options, hospital selection, and making informed decisions.',
    features: [
      'When Surgery Becomes Necessary',
      'Bypass vs PCI (Stent) Decision Support',
      'Hospital Selection Criteria',
      'Surgeon Evaluation Framework',
      'Conduit Selection Strategy',
      'Surgical Risks & Complications',
      'Insurance & Cost Considerations'
    ],
    popular: true,
    color: '#0d9488'
  },
  {
    id: 'package3',
    name: 'Advanced Diagnostic & Monitoring Consultation',
    icon: 'ri-stethoscope-line',
    description: 'Comprehensive diagnostic testing guidance and family risk screening protocols.',
    features: [
      'Basic to Advanced Cardiac Testing',
      'Blood-Based Risk Markers',
      'Imaging & Functional Testing',
      'Family Risk Screening'
    ],
    color: '#f04e58'
  },
  {
    id: 'package4',
    name: 'Genetics & Precision Cardiology Consultation',
    icon: 'ri-dna-line',
    description: 'Genetic testing interpretation and personalized prevention strategies based on your DNA.',
    features: [
      'Role of Genetics in Heart Disease',
      'Genetic Testing & Interpretation',
      'HeFH vs HoFH Understanding',
      'Early Screening Protocols',
      'Personalized Prevention Strategy'
    ],
    color: '#0d9488'
  },
  {
    id: 'package5',
    name: 'Pre- & Post-Surgery Lifestyle & Recovery Optimization',
    icon: 'ri-heart-add-line',
    description: 'Complete preparation and recovery support for optimal surgical outcomes.',
    features: [
      'Pre-PCI Preparation',
      'Pre-Bypass Preparation',
      'Post-Surgery Recovery Essentials',
      'Cardiac Rehabilitation',
      'Mental Health & Recovery',
      'Diabetes & Heart Risk Integration'
    ],
    color: '#f04e58'
  },
  {
    id: 'package6',
    name: 'Bypass Surgery Cost & Insurance Advisory (USA)',
    icon: 'ri-money-dollar-circle-line',
    description: 'Financial planning and insurance navigation for bypass surgery in the United States.',
    features: [
      'Bypass Cost Breakdown',
      'Insurance Types & Coverage',
      'Covered vs Non-Covered Services',
      'Financial Planning Strategy',
      'Institution Selection by Budget'
    ],
    color: '#0d9488'
  },
  {
    id: 'package7',
    name: 'Disease Progression Control & Prevention Strategy',
    icon: 'ri-shield-cross-line',
    description: 'Comprehensive disease management and prevention through medication and lifestyle optimization.',
    features: [
      'Medication Optimization',
      'Injectable Therapies',
      'Lifestyle Prescription',
      'Insulin Resistance Management',
      'Exercise Strategy'
    ],
    color: '#f04e58'
  },
  {
    id: 'package8',
    name: 'Continuous Monitoring & Course Correction Program',
    icon: 'ri-line-chart-line',
    description: 'Ongoing monitoring protocols and risk re-stratification for long-term heart health.',
    features: [
      'Blood Test Monitoring Schedule',
      'Imaging Follow-Up Strategy',
      'Vital Tracking (BP, Glucose, HR)',
      'Metabolic & Mental Health Tracking',
      'Ongoing Risk Re-Stratification'
    ],
    color: '#0d9488'
  },
  {
    id: 'package9',
    name: 'FH Family Tree & Genetic Screening Program',
    icon: 'ri-parent-line',
    description: 'Comprehensive family genetic screening and FH risk identification across generations.',
    features: [
      'Three-Generation Pedigree Mapping',
      'FH Risk Identification Criteria',
      'Advanced Lipid Testing',
      'Genetic Panel Testing',
      'HeFH vs HoFH Classification',
      'Cascade Family Screening',
      'Pediatric Screening Protocol'
    ],
    color: '#f04e58'
  }
];

const steps = [
  {
    number: '01',
    title: 'Choose Your Packages',
    description: 'Select one or more consultation packages that match your health needs.',
    icon: 'ri-checkbox-multiple-line'
  },
  {
    number: '02',
    title: 'Schedule a Time',
    description: 'Pick a convenient date and time from our available consultation slots.',
    icon: 'ri-calendar-check-line'
  },
  {
    number: '03',
    title: 'Meet Your Expert',
    description: 'Connect with our specialist for personalized cardiovascular guidance.',
    icon: 'ri-video-chat-line'
  }
];

export default function ConsultationPage() {
  const [selectedPackages, setSelectedPackages] = useState<string[]>([]);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [showIntakeForm, setShowIntakeForm] = useState(false);
  const [showCalendly, setShowCalendly] = useState(false);
  const [calendlyLoaded, setCalendlyLoaded] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [expandedCard, setExpandedCard] = useState<string | null>(null);
  const calendlyContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const existingScript = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]');
    if (existingScript) {
      setCalendlyLoaded(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    script.onload = () => setCalendlyLoaded(true);
    document.body.appendChild(script);

    const link = document.createElement('link');
    link.href = 'https://assets.calendly.com/assets/external/widget.css';
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    return () => {
      const s = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]');
      if (s) document.body.removeChild(s);
      const l = document.querySelector('link[href="https://assets.calendly.com/assets/external/widget.css"]');
      if (l) document.head.removeChild(l);
    };
  }, []);

  useEffect(() => {
    if (showCalendly && calendlyLoaded && calendlyContainerRef.current) {
      const selectedNames = selectedPackages
        .map(id => packages.find(p => p.id === id)?.name)
        .filter(Boolean)
        .join(', ');
      calendlyContainerRef.current.innerHTML = '';
      if ((window as any).Calendly) {
        (window as any).Calendly.initInlineWidget({
          url: `${CALENDLY_URL}?hide_gdpr_banner=1&primary_color=f04e58&a1=${encodeURIComponent(selectedNames)}`,
          parentElement: calendlyContainerRef.current,
        });
      }
    }
  }, [showCalendly, calendlyLoaded, selectedPackages]);

  const togglePackage = (id: string) => {
    setSelectedPackages(prev =>
      prev.includes(id)
        ? prev.filter(p => p !== id)
        : [...prev, id]
    );
  };

  const handleScheduleClick = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      setShowAuthPrompt(true);
      return;
    }

    if (selectedPackages.length === 0) {
      alert('Please select at least one consultation package');
      return;
    }

    setShowConsentModal(true);
  };

  const handleConsentAccepted = () => {
    setShowConsentModal(false);
    setShowIntakeForm(true);
  };

  const handleIntakeFormSubmit = () => {
    setShowIntakeForm(false);
    setShowCalendly(true);
  };

  const handleConsentDecline = () => {
    setShowConsentModal(false);
  };

  const handleGoToSignIn = () => {
    navigate('/auth', { state: { from: location }, replace: false });
  };

  const getSelectedPackageNames = () => {
    return selectedPackages
      .map(id => packages.find(p => p.id === id)?.name)
      .join(', ');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Consent Modal */}
      <ConsentModal
        isOpen={showConsentModal}
        onClose={() => setShowConsentModal(false)}
        onAccept={handleConsentAccepted}
      />

      <IntakeForm
        isOpen={showIntakeForm}
        onClose={() => setShowIntakeForm(false)}
        onSubmit={handleIntakeFormSubmit}
      />

      {/* Auth Prompt Modal */}
      {showAuthPrompt && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowAuthPrompt(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden animate-[fadeIn_0.25s_ease-out]" onClick={e => e.stopPropagation()}>
            <div className="relative overflow-hidden p-8 text-center" style={{ background: 'linear-gradient(135deg, #f04e58 0%, #d93d47 100%)' }}>
              <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2"></div>
              <div className="relative z-10">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
                  <i className="ri-lock-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-white">Sign In to Continue</h3>
                <p className="text-white/80 text-sm mt-2">Create an account to schedule your consultation</p>
              </div>
            </div>
            <div className="p-6">
              <div className="bg-[#fef5f5] rounded-xl p-4 mb-6">
                <div className="flex items-center gap-3">
                  <i className="ri-shield-check-line text-[#f04e58] text-lg"></i>
                  <p className="text-sm text-gray-600">Your selected packages will be saved after signing in.</p>
                </div>
              </div>
              <div className="flex flex-col gap-3">
                <button
                  onClick={handleGoToSignIn}
                  className="w-full bg-[#f04e58] hover:bg-[#d93d47] text-white py-3.5 rounded-xl font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow-lg shadow-[#f04e58]/20"
                >
                  <i className="ri-user-line"></i>
                  Sign In / Create Account
                </button>
                <button
                  onClick={() => setShowAuthPrompt(false)}
                  className="w-full text-gray-500 hover:text-gray-700 py-2.5 text-sm transition-colors cursor-pointer whitespace-nowrap"
                >
                  Continue Browsing
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navbar */}
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-28 pb-20 overflow-hidden" style={{ background: 'linear-gradient(135deg, #fef3f2 0%, #fde8e7 50%, #fdd8d6 100%)' }}>
        <div className="absolute top-20 right-0 w-96 h-96 bg-[#f04e58]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-[#0d9488]/5 rounded-full blur-3xl"></div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h1 className="text-5xl md:text-6xl font-light text-gray-900 mb-6 leading-tight">
                Book Your <strong className="font-semibold text-[#f04e58]">Consultation</strong>
              </h1>

              <p className="text-lg text-gray-600 leading-relaxed font-light max-w-lg mb-10">
                Choose the consultation packages that best fit your needs. Our expert cardiologists provide specialized guidance for every aspect of your cardiovascular health journey.
              </p>

              <div className="flex items-center gap-6">
                <a
                  href="#packages"
                  className="inline-flex items-center px-8 py-4 bg-[#0d9488] text-white rounded-xl font-medium hover:bg-[#0f766e] transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                >
                  Explore Packages
                  <i className="ri-arrow-down-line ml-2"></i>
                </a>
              </div>
            </div>

            <div className="relative hidden lg:block">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/3431ab92f2a6342853ab7d40ffdbf29f.jpg"
                  alt="Cardiovascular health consultation"
                  className="w-full h-[500px] object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-5 max-w-[220px]">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#0d9488]/10">
                    <i className="ri-user-heart-line text-xl text-[#0d9488]"></i>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">9</p>
                    <p className="text-xs text-gray-500 font-light">Specialized Packages</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="px-4 py-2 bg-[#0d9488]/10 text-[#0d9488] rounded-full text-sm font-semibold">Simple Process</span>
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mt-6 mb-4">
              How It <strong className="font-semibold">Works</strong>
            </h2>
            <p className="text-gray-600 font-light max-w-xl mx-auto">Three simple steps to get personalized cardiovascular guidance</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative group">
                <div className="bg-white rounded-2xl p-8 border border-gray-100 hover:border-[#0d9488]/30 hover:shadow-xl transition-all duration-500 h-full">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br from-[#0d9488]/10 to-[#0d9488]/5 group-hover:scale-110 transition-transform duration-500">
                      <i className={`${step.icon} text-2xl text-[#0d9488]`}></i>
                    </div>
                    <span className="text-4xl font-extrabold text-[#0d9488] group-hover:text-[#0d9488]/80 transition-colors">{step.number}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-sm text-gray-600 font-light leading-relaxed">{step.description}</p>
                </div>
                {index < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <i className="ri-arrow-right-line text-gray-300 text-xl"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section id="packages" className="py-20" style={{ background: 'linear-gradient(180deg, #fef3f2 0%, #ffffff 100%)' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="px-4 py-2 bg-[#f04e58]/10 text-[#f04e58] rounded-full text-sm font-semibold">Consultation Packages</span>
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mt-6 mb-4">
              Select Your <strong className="font-semibold text-[#f04e58]">Package(s)</strong>
            </h2>
            <p className="text-gray-600 font-light max-w-2xl mx-auto">Choose one or more packages tailored to your cardiovascular health needs. Each package covers specific areas of expertise.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {packages.map((pkg) => {
              const isSelected = selectedPackages.includes(pkg.id);
              const isExpanded = expandedCard === pkg.id;
              return (
                <div
                  key={pkg.id}
                  className={`relative bg-white rounded-2xl transition-all duration-500 group ${
                    isSelected
                      ? 'ring-2 ring-[#f04e58] shadow-xl shadow-[#f04e58]/10'
                      : 'border border-gray-100 hover:border-gray-200 hover:shadow-lg'
                  }`}
                >
                  {pkg.popular && (
                    <div className="absolute -top-3 left-6 z-10">
                      <div className="bg-gradient-to-r from-[#0d9488] to-[#14b8a6] text-white text-xs font-semibold px-4 py-1.5 rounded-full shadow-lg shadow-[#0d9488]/20">
                        <i className="ri-star-fill mr-1"></i>
                        Most Popular
                      </div>
                    </div>
                  )}

                  <div className="p-6">
                    <div className="flex items-start justify-between mb-5">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 ${
                          isSelected ? 'bg-[#f04e58]/10' : 'bg-gray-50 group-hover:bg-[#f04e58]/5'
                        }`}>
                          <i className={`${pkg.icon} text-2xl ${isSelected ? 'text-[#f04e58]' : 'text-gray-400 group-hover:text-[#f04e58]'} transition-colors`}></i>
                        </div>
                        <span
                          className="px-2.5 py-1 text-xs font-semibold rounded-lg"
                          style={{ backgroundColor: `${pkg.color}15`, color: pkg.color }}
                        >
                          Package {packages.findIndex(p => p.id === pkg.id) + 1}
                        </span>
                      </div>

                      <button
                        onClick={() => togglePackage(pkg.id)}
                        className={`w-7 h-7 rounded-lg border-2 flex items-center justify-center transition-all duration-300 cursor-pointer ${
                          isSelected
                            ? 'bg-[#f04e58] border-[#f04e58] scale-110'
                            : 'border-gray-300 hover:border-[#f04e58]'
                        }`}
                      >
                        {isSelected && (
                          <i className="ri-check-line text-white text-sm"></i>
                        )}
                      </button>
                    </div>

                    <h3 className="text-base font-semibold text-gray-900 mb-2 leading-snug min-h-[44px] cursor-pointer" onClick={() => togglePackage(pkg.id)}>
                      {pkg.name}
                    </h3>
                    <p className="text-sm text-gray-500 font-light leading-relaxed mb-4">{pkg.description}</p>

                    <div className={`overflow-hidden transition-all duration-500 ${isExpanded ? 'max-h-[500px]' : 'max-h-[120px]'}`}>
                      <ul className="space-y-2.5">
                        {pkg.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2.5 text-sm">
                            <div className="w-5 h-5 flex items-center justify-center rounded-full bg-[#0d9488]/10 flex-shrink-0 mt-0.5">
                              <i className="ri-check-line text-[#0d9488] text-xs"></i>
                            </div>
                            <span className="text-gray-700 font-light">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {pkg.features.length > 4 && (
                      <button
                        onClick={() => setExpandedCard(isExpanded ? null : pkg.id)}
                        className="mt-3 text-xs text-[#0d9488] hover:text-[#0f766e] font-medium flex items-center gap-1 cursor-pointer transition-colors"
                      >
                        {isExpanded ? 'Show less' : `+${pkg.features.length - 4} more`}
                        <i className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line`}></i>
                      </button>
                    )}
                  </div>

                  <div
                    onClick={() => togglePackage(pkg.id)}
                    className={`px-6 py-3.5 border-t cursor-pointer transition-all duration-300 flex items-center justify-center gap-2 ${
                      isSelected
                        ? 'bg-[#f04e58]/5 border-[#f04e58]/20 text-[#f04e58]'
                        : 'bg-gray-50/50 border-gray-100 text-gray-500 hover:text-[#f04e58] hover:bg-[#fef5f5]'
                    }`}
                  >
                    <i className={`${isSelected ? 'ri-checkbox-circle-fill' : 'ri-add-circle-line'} text-lg`}></i>
                    <span className="text-sm font-medium">{isSelected ? 'Selected' : 'Select Package'}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Floating Selection Bar */}
          {selectedPackages.length > 0 && !showCalendly && (
            <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 w-full max-w-2xl px-4">
              <div className="bg-gray-900 rounded-2xl shadow-2xl p-4 flex items-center justify-between gap-4 backdrop-blur-sm">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#f04e58] flex-shrink-0">
                    <span className="text-white font-bold text-sm">{selectedPackages.length}</span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-white text-sm font-medium whitespace-nowrap">
                      {selectedPackages.length === 1 ? '1 package selected' : `${selectedPackages.length} packages selected`}
                    </p>
                    <p className="text-gray-400 text-xs truncate">
                      {selectedPackages.map(id => packages.find(p => p.id === id)?.name).join(', ')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleScheduleClick}
                  className="bg-[#f04e58] hover:bg-[#d93d47] text-white px-6 py-3 rounded-xl font-semibold transition-all whitespace-nowrap cursor-pointer flex items-center gap-2 shadow-lg shadow-[#f04e58]/30 flex-shrink-0"
                >
                  <i className="ri-calendar-line"></i>
                  Schedule Now
                  {!user && <i className="ri-lock-line text-xs opacity-70"></i>}
                </button>
              </div>
            </div>
          )}

          {/* Inline Selected Summary (visible when Calendly is shown) */}
          {selectedPackages.length > 0 && showCalendly && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-12 shadow-sm">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <i className="ri-checkbox-circle-fill text-[#0d9488]"></i>
                    Selected Packages ({selectedPackages.length})
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedPackages.map(id => {
                      const pkg = packages.find(p => p.id === id);
                      return (
                        <span key={id} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#fef5f5] rounded-full text-sm text-gray-700">
                          <i className={`${pkg?.icon} text-[#f04e58] text-xs`}></i>
                          {pkg?.name}
                        </span>
                      );
                    })}
                  </div>
                </div>
                <button
                  onClick={() => setShowCalendly(false)}
                  className="text-sm text-[#f04e58] hover:text-[#d93d47] font-medium flex items-center gap-1 cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-edit-line"></i>
                  Modify Selection
                </button>
              </div>
            </div>
          )}

          {/* Calendly Section */}
          {showCalendly && (
            <div id="calendly-section" className="max-w-4xl mx-auto">
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
                <div className="relative overflow-hidden p-8" style={{ background: 'linear-gradient(135deg, #f04e58 0%, #d93d47 100%)' }}>
                  <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                  <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2"></div>
                  <div className="relative z-10">
                    <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-3">
                      <i className="ri-calendar-check-line"></i>
                      Schedule Your Consultation
                    </h2>
                    <p className="text-white/80 font-light">
                      Select a convenient date and time. You&apos;ll receive a confirmation email with all the details.
                    </p>
                  </div>
                </div>

                <div
                  ref={calendlyContainerRef}
                  style={{ minWidth: '320px', height: '700px' }}
                  className="relative"
                >
                  {!calendlyLoaded && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-white">
                      <div className="w-10 h-10 border-4 border-[#f04e58]/20 border-t-[#f04e58] rounded-full animate-spin mb-4"></div>
                      <p className="text-gray-500 text-sm">Loading scheduling calendar...</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Empty State */}
          {selectedPackages.length === 0 && (
            <div className="text-center py-12">
              <div className="w-20 h-20 flex items-center justify-center rounded-full bg-gray-50 mx-auto mb-4">
                <i className="ri-checkbox-circle-line text-3xl text-gray-300"></i>
              </div>
              <p className="text-gray-400 font-light">Select at least one package above to proceed</p>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
